import React, { useCallback, useEffect, useRef, useState } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useIntl } from 'react-intl';

import { OutlinedInput } from '../subcomponents';
import styles from './AppointmentNoteField.module.scss';

const IDLE_DEBOUNCE_MS = 800;
const SAVED_INDICATOR_MS = 2000;

type AppointmentNoteFieldProps = {
  initialNote: string;
  onSave: (note: string) => Promise<void>;
  disabled?: boolean;
  dataTestId?: string;
};

type IndicatorState = 'idle' | 'saved' | 'error';

function AppointmentNoteFieldComponent({
  initialNote,
  onSave,
  disabled,
  dataTestId,
}: AppointmentNoteFieldProps) {
  const intl = useIntl();

  const [value, setValue] = useState(initialNote);
  const [indicator, setIndicator] = useState<IndicatorState>('idle');

  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSavedRef = useRef(initialNote);
  const inFlightRef = useRef(0);

  const clearIdleTimer = useCallback(() => {
    if (idleTimerRef.current !== null) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  }, []);

  const clearSavedTimer = useCallback(() => {
    if (savedTimerRef.current !== null) {
      clearTimeout(savedTimerRef.current);
      savedTimerRef.current = null;
    }
  }, []);

  const persist = useCallback(async (next: string) => {
    if (next === lastSavedRef.current) {
      return;
    }

    inFlightRef.current += 1;
    const ticket = inFlightRef.current;

    try {
      await onSave(next);

      const isStale = ticket !== inFlightRef.current;
      if (isStale) {
        return;
      }

      lastSavedRef.current = next;
      setIndicator('saved');
      clearSavedTimer();
      savedTimerRef.current = setTimeout(() => {
        setIndicator('idle');
      }, SAVED_INDICATOR_MS);
    } catch (error) {
      const isStale = ticket !== inFlightRef.current;
      if (isStale) {
        return;
      }

      setIndicator('error');
    }
  }, [onSave, clearSavedTimer]);

  const handleChange = useCallback((next: string | null) => {
    let text = '';
    if (next !== null) {
      text = next;
    }
    setValue(text);

    clearIdleTimer();
    idleTimerRef.current = setTimeout(() => {
      persist(text);
    }, IDLE_DEBOUNCE_MS);
  }, [clearIdleTimer, persist]);

  const handleBlur = useCallback(() => {
    clearIdleTimer();
    persist(value);
  }, [clearIdleTimer, persist, value]);

  useEffect(() => () => {
    clearIdleTimer();
    clearSavedTimer();
  }, [clearIdleTimer, clearSavedTimer]);

  let indicatorNode: React.ReactNode = null;
  if (indicator === 'saved') {
    indicatorNode = (
      <span className={`${styles.indicator} ${styles.indicatorSaved}`} data-testid="note-indicator-saved">
        <CheckIcon className={styles.indicatorIcon} />
        <span>{intl.formatMessage({ id: 'common.saved' })}</span>
      </span>
    );
  } else if (indicator === 'error') {
    indicatorNode = (
      <span className={`${styles.indicator} ${styles.indicatorError}`} data-testid="note-indicator-error">
        <ErrorOutlineIcon className={styles.indicatorIcon} />
        <span>{intl.formatMessage({ id: 'common.couldNotSave' })}</span>
      </span>
    );
  } else {
    indicatorNode = <span className={styles.indicator} aria-hidden="true" />;
  }

  return (
    <div className={styles.wrapper}>
      <OutlinedInput
        dataTestId={dataTestId}
        placeholder={intl.formatMessage({ id: 'appointment.notes' })}
        value={value}
        multiline
        disabled={disabled}
        onChange={handleChange}
        onBlur={handleBlur}
      />
      {indicatorNode}
    </div>
  );
}

AppointmentNoteFieldComponent.defaultProps = {
  disabled: false,
  dataTestId: 'appointment-note-input',
};

export const AppointmentNoteField = React.memo(AppointmentNoteFieldComponent);
